// @flow

import type { B2bClientInterface } from './b2b-client-interface'
import type { Company } from '../../types/company'
import AbstractAuthenticatedClient from '../abstract-authenticated-client'
import type { CardDetails } from '../../types/card-details'
import type { CardsPage } from '../../types/cards-page'
import {
  ROUTE_PATH_API_COMPANY,
  ROUTE_PATH_API_CARDS,
  ROUTE_PATH_API_COMPANY_CONTACT_DETAILS,
  ROUTE_PATH_API_COMPANY_ADDRESSES,
  ROUTE_PATH_API_CARD_TLS_PRODUCTS,
  ROUTE_PATH_API_CARD_ROR_PRODUCTS,
  ROUTE_PATH_API_INVOICES,
  ROUTE_PATH_API_CARD_TRIPS,
  ROUTE_PATH_API_CARD_TRIPS_DOWNLOAD_EXPORT,
  ROUTE_PATH_API_CARD_TRIP_FILTER_MONTHS,
  ROUTE_PATH_API_INVOICE_DOCUMENT_DOWNLOAD,
  ROUTE_PATH_API_ORDERS,
  ROUTE_PATH_API_ORDER_ITEMS,
  ROUTE_PATH_API_ORDER_REPLACEMENT_CARDS,
  ROUTE_PATH_API_ORDER_TERMINATE_CARDS,
  ROUTE_PATH_API_COMPANY_SELF_SERVICE_STATUS
} from '../../../src-server/api-routes'
import type { TlsProductsPage } from '../../types/tls-products-page'
import { normalizeTlsProductsPage } from './normalizer/TlsProductsPageNormalizer'
import type { RorProductsPage } from '../../types/ror-products-page'
import { normalizeRorProductsPage } from './normalizer/RorProductsPageNormalizer'
import { normalizeTripsPage } from './normalizer/TripsPageNormalizer'
import { normalizeInvoicesPage } from './normalizer/invoices-page-normalizer'
import { normalizeOrdersPage } from './normalizer/orders-page-normalizer'
import type { InvoicesPage } from '../../types/invoices-page'
import type { TripsPage } from '../../types/trips-page'
import type { MonthList } from '../../types/month-list'
import type { InvoiceSummary } from '../../types/invoice-summary'
import type { InvoiceDocument } from '../../types/invoice-document'
import { buildValidationErrorResult } from '../../types/validation-error-result'
import GenericError from '../error/generic-error'
import ValidationError from '../error/validation-error'
import type {OrdersPage} from '../../types/orders-page'
import {normalizeOrderItemsPage} from './normalizer/orders-items-page-normalizer'
import type {OrderItemsPage} from '../../types/orders-items-page'
import {normalizeCardsPage} from './normalizer/cards-page-normalizer'
import type {OrderReplacementCardRequest} from '../../types/order-replacement-card-request'
import type {OrderTerminateCardRequest} from '../../types/order-terminate-card-request'
import type {CompanyCardsFilter} from '../../types/company-cards-filter'
import {normalizeOrderReplacementCardRequest} from './normalizer/order-replacement-card-request-normalizer'
import type {CompanySelfServiceStatus} from '../../types/company-self-service-status'
const camelCase = require('camelcase')
const camelcaseKeys = require('camelcase-keys')
const snakeCaseKeys = require('snakecase-keys')

class B2bClient extends AbstractAuthenticatedClient implements B2bClientInterface {
  async getCompany (): Promise<Company> {
    return this.normalizeResponse(await this.doGet(`/api${ROUTE_PATH_API_COMPANY}`))
  }

  async getCompanyInvoices (
    page: number,
    pageSize: number,
    invoiceNumber: string,
    year: string,
    month: string,
    date: string,
    hasSpecification: boolean,
    orderBy: ?string,
    orderDirection: ?string
  ): Promise<InvoicesPage> {
    let params = {
      page: page,
      size: pageSize,
      invoiceNumber: invoiceNumber,
      year: year,
      month: month,
      date: date,
      hasSpecification: hasSpecification
    }
    params = Object.assign(this.getOrderingParams(orderBy, orderDirection), params)
    const response = this.normalizeResponse(await this.doGet(`/api${ROUTE_PATH_API_INVOICES}`, params))
    return normalizeInvoicesPage(response)
  }

  async getCompanyCards (
    page: number,
    size: number,
    filter: CompanyCardsFilter,
    orderBy: ?string,
    orderDirection: ?string,
    includeCustomerDetails: boolean = false,
    requireCardContract: boolean = false
  ): Promise<CardsPage> {
    let params = {
      page,
      size,
      includeCustomerDetails,
      requireCardContract
    }

    for (let key in filter) {
      if (filter[key] !== null && filter[key] !== '') {
        params[key] = filter[key]
      }
    }

    params = {...this.getOrderingParams(orderBy, orderDirection), ...params}
    const response = this.normalizeResponse(await this.doGet(`/api${ROUTE_PATH_API_CARDS}`, this.normalizeRequest(params)))

    return normalizeCardsPage(response)
  }

  async getCompanySelfServiceStatus (): Promise<CompanySelfServiceStatus> {
    return this.normalizeResponse(await this.doGet(`/api${ROUTE_PATH_API_COMPANY_SELF_SERVICE_STATUS}`))
  }

  async getCardDetails (cardId: number): Promise<CardDetails> {
    try {
      return this.normalizeResponse(await this.doGet(`/api/card/${cardId}`))
    } catch (error) {
      throw B2bClient.convertValidationError(error)
    }
  }

  async postOrderReplacementCards (orderReplacementCardRequest: OrderReplacementCardRequest): Promise<void> {
    try {
      await this.doPost(
        `/api${ROUTE_PATH_API_ORDER_REPLACEMENT_CARDS}`,
        this.normalizeRequest(normalizeOrderReplacementCardRequest(orderReplacementCardRequest))
      )
    } catch (error) {
      throw B2bClient.convertValidationError(error)
    }
  }

  async postOrderTerminateCards (orderTerminateCardRequest: OrderTerminateCardRequest): Promise<void> {
    try {
      await this.doPost(`/api${ROUTE_PATH_API_ORDER_TERMINATE_CARDS}`, this.normalizeRequest(orderTerminateCardRequest))
    } catch (error) {
      throw B2bClient.convertValidationError(error)
    }
  }

  async getCardTlsProducts (
    cardId: number,
    page: number,
    pageSize: number,
    orderBy: ?string,
    orderDirection: ?string
  ): Promise<TlsProductsPage> {
    let params = {
      page,
      pageSize
    }
    params = Object.assign(this.getOrderingParams(orderBy, orderDirection), params)
    const response = this.normalizeResponse(await this.doGet(
      `/api${ROUTE_PATH_API_CARD_TLS_PRODUCTS}`
        .replace(':cardId', ((cardId: any): string))
        .replace(':page', ((page: any): string))
        .replace(':pageSize', ((pageSize: any): string)),
      this.normalizeRequest(params)))
    return normalizeTlsProductsPage(response)
  }

  async getCardRorProducts (
    cardId: number,
    page: number,
    pageSize: number,
    orderBy: ?string,
    orderDirection: ?string
  ): Promise<RorProductsPage> {
    let params = {
      page,
      pageSize
    }
    params = Object.assign(this.getOrderingParams(orderBy, orderDirection), params)
    const response = this.normalizeResponse(await this.doGet(
      `/api${ROUTE_PATH_API_CARD_ROR_PRODUCTS}`
        .replace(':cardId', ((cardId: any): string))
        .replace(':page', ((page: any): string))
        .replace(':pageSize', ((pageSize: any): string)),
      this.normalizeRequest(params)))
    return normalizeRorProductsPage(response)
  }

  async downloadInvoiceDocumentFile (invoice: InvoiceSummary, document: InvoiceDocument): Promise<void> {
    return this.downloadFile(
      `/api${ROUTE_PATH_API_INVOICE_DOCUMENT_DOWNLOAD}`
        .replace(':invoiceNumber', invoice.invoiceNumber)
        .replace(':documentIdentifier', document.identifier),
      {},
      document.identifier
    )
  }

  async downloadFile (path: string, params: Object, fileName: string): Promise<void> {
    const response = await this.doGet(path, params, { responseType: 'blob' })
    const url = window.URL.createObjectURL(new Blob([response]))
    const link = window.document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName)
    window.document.body.appendChild(link)
    link.click()
    window.document.body.removeChild(link)
  }

  async getCardTrips (
    cardId: number, page: number, pageSize: number, invoiceNumber: string, checkinMonth: string, checkinDateFrom: ?Date,
    checkinDateTo: ?Date, orderBy: ?string, orderDirection: ?string
  ): Promise<TripsPage> {
    let params = {
      page,
      pageSize,
      invoiceNumber,
      checkinMonth,
      checkinDateFrom,
      checkinDateTo
    }
    params = Object.assign(this.getOrderingParams(orderBy, orderDirection), params)
    const response = this.normalizeResponse(await this.doGet(
      `/api${ROUTE_PATH_API_CARD_TRIPS}`
        .replace(':cardId', ((cardId: any): string))
        .replace(':page', ((page: any): string))
        .replace(':pageSize', ((pageSize: any): string)),
      this.normalizeRequest(params)))
    return normalizeTripsPage(response)
  }

  async downloadCardTripsExportFile (
    cardId: number,
    invoiceNumber: string,
    checkinMonth: string,
    checkinDateFrom: ?Date,
    checkinDateTo: ?Date
  ): Promise<void> {
    return this.downloadFile(
      `/api${ROUTE_PATH_API_CARD_TRIPS_DOWNLOAD_EXPORT}`.replace(':cardId', ((cardId: any): string)),
      this.normalizeRequest({
        invoiceNumber,
        checkinMonth,
        checkinDateFrom,
        checkinDateTo
      }),
      'ritten.csv'
    )
  }

  async getCardTripFilterMonths (cardId: number): Promise<MonthList> {
    return this.normalizeResponse(await this.doGet(
      `/api${ROUTE_PATH_API_CARD_TRIP_FILTER_MONTHS}`
        .replace(':cardId', ((cardId: any): string))))
  }

  async patchCompanyContactDetails (company: Company): Promise<void> {
    try {
      await this.doPatch(`/api${ROUTE_PATH_API_COMPANY_CONTACT_DETAILS}`, this.normalizeRequest(company))
    } catch (error) {
      throw B2bClient.convertValidationError(error)
    }
  }

  async patchCompanyAddresses (company: Company): Promise<void> {
    try {
      await this.doPatch(`/api${ROUTE_PATH_API_COMPANY_ADDRESSES}`, this.normalizeRequest(company))
    } catch (error) {
      throw B2bClient.convertValidationError(error)
    }
  }

  async getOrders (
    page: number,
    pageSize: number,
    orderBy: ?string,
    orderDirection: ?string
  ): Promise<OrdersPage> {
    let params = {
      page: page,
      size: pageSize
    }

    params = {...this.getOrderingParams(orderBy, orderDirection), ...params}
    const response = this.normalizeResponse(await this.doGet(`/api${ROUTE_PATH_API_ORDERS}`, params))

    return normalizeOrdersPage(response)
  }

  async getOrderItems (
    orderId: number,
    page: number,
    pageSize: number,
    orderBy: ?string,
    orderDirection: ?string
  ): Promise<OrderItemsPage> {
    let params = {
      page: page,
      size: pageSize
    }

    params = {...this.getOrderingParams(orderBy, orderDirection), ...params}
    const response = this.normalizeResponse(
      await this.doGet(
        `/api${ROUTE_PATH_API_ORDER_ITEMS}`.replace(':b2bOrderId', ((orderId: any): string)),
        params
      )
    )

    return normalizeOrderItemsPage(response)
  }

  normalizeResponse (response: Object): Object {
    return camelcaseKeys(response, { deep: true })
  }

  normalizeRequest (request: Object): Object {
    return snakeCaseKeys(request, { deep: true })
  }

  static convertValidationError (error: Object): GenericError|ValidationError {
    if (error.code && error.response && error.response['validation_errors']) {
      return new ValidationError(
        error.code,
        error.response,
        buildValidationErrorResult(
          error.response['validation_errors'].map((item) => {
            item.path = camelCase(item.path)
            return item
          }))
      )
    }
    return error
  }

  getOrderingParams (orderBy: ?string, orderDirection: ?string): Object {
    if (orderBy !== null) {
      return { orderBy, orderDirection }
    }

    return {}
  }
}

export default B2bClient



// WEBPACK FOOTER //
// ./src/services/b2b/b2b-client.js